/**
 * @module reconcile-disability-map
 * Shared utility to reconcile a map of disability IDs to values based on the current set of disabilities.
 * Handles orphan removal, optional atomic rename migration, and preserved keys.
 *
 * Exports {@link upgradeLegacyKeys} and {@link reconcileDisabilityMap}.
 */

import set from 'platform/utilities/data/set';

import { sippableId, sippableIdForCondition } from './index';

/**
 * Moves values saved under a key that predates the side of body onto the
 * current key(s). A name claimed on more than one side fans out to each,
 * because a single checkbox once covered the whole group.
 *
 * @param {Object} map - Disability id to value
 * @param {Array} rows - Current newDisabilities entries
 * @param {string[]} [preservedKeys] - Keys that are never disability ids
 * @returns {Object} the map, unchanged by reference when nothing moved
 */
export function upgradeLegacyKeys(map, rows = [], preservedKeys = []) {
  if (!map || typeof map !== 'object') return map;
  if (!Array.isArray(rows) || rows.length === 0) return map;

  const currentIds = new Set(rows.map(sippableIdForCondition));

  // Map, not an object: names are free text and sippableId keeps underscores,
  // so a name of "__proto__" would read back Object.prototype.
  const claimants = new Map();
  rows.forEach(row => {
    const condition =
      typeof row?.condition === 'string' ? row.condition.trim() : '';
    if (!condition) return;

    const legacyId = sippableId(condition);
    const currentId = sippableIdForCondition(row);
    if (legacyId === currentId) return;

    claimants.set(legacyId, (claimants.get(legacyId) || []).concat(currentId));
  });
  if (claimants.size === 0) return map;

  const next = { ...map };
  let changed = false;

  Object.keys(map).forEach(key => {
    if (preservedKeys.includes(key) || currentIds.has(key)) return;

    const targets = claimants.get(key);
    if (!targets) return;

    let moved = false;
    targets.forEach(targetId => {
      // Never overwrite an answer the Veteran has already given.
      if (next[targetId] === undefined) {
        next[targetId] = map[key];
        moved = true;
      }
    });

    if (moved) {
      delete next[key];
      changed = true;
    }
  });

  return changed ? next : map;
}

/**
 * Reconciles a map of disability IDs to values based on the current set of disabilities.
 *
 * @param {Object} params
 *   - oldFormData: previous form data (for rename detection)
 *   - newFormData: new form data (to update)
 *   - mapPath: string path to the map in formData (e.g., 'toxicExposure.conditions')
 *   - getDisabilities: function(formData) => array of disability objects
 *   - keyForDisability: function(disability) => string (ID for map)
 *   - preservedKeys: array of string keys to always preserve (e.g., ['none'])
 *   - migrateRenames: boolean (if true, migrate values for renames when array length is unchanged)
 * @returns {Object} updated newFormData
 */
export function reconcileDisabilityMap({
  oldFormData,
  newFormData,
  mapPath,
  getDisabilities,
  keyForDisability,
  preservedKeys = [],
  migrateRenames = false,
}) {
  // Safely navigate nested paths, returning undefined if any step is null/undefined
  let map = mapPath.split('.').reduce((obj, key) => {
    if (obj === null || obj === undefined) return undefined;
    return obj[key];
  }, newFormData);

  // If the map is missing, treat as empty object and ensure it gets set
  let mapWasMissing = false;
  if (!map) {
    map = {};
    mapWasMissing = true;
  }

  const oldDisabilities = oldFormData ? getDisabilities(oldFormData) : [];
  const newDisabilities = getDisabilities(newFormData) || [];

  const next = { ...map };
  let changed = false;

  // Migrate values for renames if enabled, oldFormData is provided, and array length is unchanged
  if (
    migrateRenames &&
    oldFormData &&
    oldDisabilities.length === newDisabilities.length
  ) {
    const renames = [];
    for (let i = 0; i < newDisabilities.length; i += 1) {
      const oldId = keyForDisability(oldDisabilities[i]);
      const newId = keyForDisability(newDisabilities[i]);
      if (oldId !== newId && map[oldId] !== undefined) {
        renames.push({ oldId, newId, value: map[oldId] });
      }
    }
    if (renames.length > 0) {
      renames.forEach(({ oldId }) => {
        delete next[oldId];
      });
      renames.forEach(({ newId, value }) => {
        next[newId] = value;
      });
      changed = true;
    }
  }

  // Remove orphaned keys (not in newDisabilities and not preserved)
  const validIds = new Set(newDisabilities.map(keyForDisability));
  Object.keys(next).forEach(key => {
    if (!validIds.has(key) && !preservedKeys.includes(key)) {
      delete next[key];
      changed = true;
    }
  });

  // If nothing changed and the map wasn't missing, return unchanged
  if (!changed && !mapWasMissing) return newFormData;

  // If the map was missing and is still empty after reconciliation, return unchanged
  if (mapWasMissing && Object.keys(next).length === 0) return newFormData;

  return set(mapPath, next, newFormData);
}
